import { Card, Link, List, ListItem, ListItemText, Stack, Typography } from '@mui/material';
import React from 'react';
import { Icon } from '@iconify/react';
import { useSnackbar } from 'notistack';
import { useStripe } from '@stripe/react-stripe-js';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';

import { useTheme } from '@mui/material/styles';
import { LoadingButton } from '@mui/lab';
import { editItem } from '../../fetch';
import { useEffectAsync } from '../../reactHelper';
import Label from '../Label';
import { decodeHTMLEntities } from '../../common/Formatter';
import { useTranslation } from '../../common/LocalizationProvider';
import { sessionActions } from '../../store';
import { symbolCurrency } from './utils';
import { selectSession } from '../../createSelectorUtils';

const getSubscriptionDate = (key) => {
  const date = moment();
  if (key === 'month') {
    date.add(1, 'month');
  } else if (key === 'year') {
    date.add(1, 'year');
  } else if (key === 'quarterly') {
    date.add(3, 'month');
  } else {
    date.add(7, 'days');
  }

  return date.format('DD/MM/YYYY');
};

const getSubscriptionPer = (key) => {
  if (key === 'month') {
    return 'SPA_PerMonth';
  }
  if (key === 'quarterly') {
    return 'SPA_PerQuarterly';
  }
  return 'SPA_PerYear';
};

export default function Subscription() {
  const location = useLocation();
  const t = useTranslation();
  const stripe = useStripe();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const theme = useTheme();

  const session = useSelector(selectSession);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const features = [
    t('SPA_Features_FlightReplay'),
    t('SPA_Features_FlightDataAnalysis'),
    t('SPA_Features_LifetimeStatistics'),
    t('SPA_Features_ClubInfoRanking'),
    t('SPA_Features_UnlimitedAccess')
  ];

  const [plan, setPlan] = React.useState({
    name: '',
    color: 'default',
    price: '',
    priceID: null,
    paymentMethodID: null,
    ready: false,
    currency: ''
  });

  const handleButtonClick = async () => {
    setPlan({
      id: location.state.id,
      name: location.state.name,
      color: location.state.color,
      price: location.state.price,
      priceInterval: location.state.priceInterval,
      priceID: location.state.priceID,
      paymentMethodID: location.state.paymentMethodID,
      ready: true,
      currency: location.state.currency
    });
  };

  function handlePaymentThatRequiresCustomerAction({
    subscription,
    invoice,
    priceId,
    paymentMethodId,
    isRetry
  }) {
    if (subscription && (subscription.status === 'active' || subscription.status === 'trialing')) {
      // Subscription is active, no customer actions required.
      return { subscription, priceId, paymentMethodId };
    }

    // If it's a first payment attempt, the payment intent is on the subscription latest invoice.
    // If it's a retry, the payment intent will be on the invoice itself.
    const paymentIntent = invoice
      ? invoice.payment_intent
      : subscription.latest_invoice.payment_intent;

    if (
      paymentIntent.status === 'requires_action' ||
      (isRetry === true && paymentIntent.status === 'requires_payment_method')
    ) {
      return stripe
        .confirmCardPayment(paymentIntent.client_secret, {
          payment_method: paymentMethodId
        })
        .then((result) => {
          if (result.error) {
            // Start code flow to handle updating the payment details.
            // Display error message in your UI.
            // The card was declined (i.e. insufficient funds, card has expired, etc).
            throw result;
          } else if (result.paymentIntent.status === 'succeeded') {
            // Show a success message to your customer.
            return {
              priceId,
              subscription,
              invoice,
              paymentMethodId
            };
          }

          return null;
        })
        .catch((event) => {
          enqueueSnackbar(event.error.message, { variant: 'error' });
        });
    }
    // No customer action needed.
    return { subscription, priceId, paymentMethodId };
  }

  function handleRequiresPaymentMethod({ subscription, paymentMethodId, priceId }) {
    if (subscription.status === 'active' || subscription.status === 'trialing') {
      // subscription is active, no customer actions required.
      return { subscription, priceId, paymentMethodId };
    }
    if (subscription.latest_invoice.payment_intent.status === 'requires_payment_method') {
      // Using localStorage to manage the state of the retry here,
      // feel free to replace with what you prefer.
      // Store the latest invoice ID and status.
      location.state = { ...location.state, latestInvoiceId: subscription.latest_invoice.id };
      location.state = {
        ...location.state,
        latestInvoicePaymentIntentStatus: subscription.latest_invoice.payment_intent.status
      };

      // eslint-disable-next-line no-throw-literal
      throw { error: { message: 'Your card was declined.' } };
    } else {
      return { subscription, priceId, paymentMethodId };
    }
  }

  function onSubscriptionComplete(result) {
    // Payment was successful.
    if (result.subscription.status === 'active' || result.subscription.status === 'trialing') {
      enqueueSnackbar(t('SPA_Payment_Accepted'), { variant: 'success' });
      dispatch(sessionActions.abonnement(true));
      navigate('/', { replace: true });
    } else if (result.subscription.status === 'incomplete') {
      enqueueSnackbar(t('SPA_Payment_NotAchieved'), { variant: 'error' });
    } else {
      enqueueSnackbar(t('SPA_Payment_Error'), { variant: 'error' });
    }

    return null;
  }

  useEffectAsync(async () => {
    if (
      plan.ready === true &&
      plan.priceID !== null &&
      plan.paymentMethodID !== 'null' &&
      plan.id !== 'free'
    ) {
      await editItem('abonnement/create-subscription', undefined, {
        priceId: plan.priceID,
        paymentMethodId: plan.paymentMethodID,
        currency: plan.currency
      })
        .then((response) => {
          setIsSubmitting(false);
          if (response.ok) {
            return response.json();
          }
          response.text().then((error) => {
            const message = error.slice(0, error.indexOf('-'));
            enqueueSnackbar(message, { variant: 'error' });
          });

          return null;
        })
        // If the card is declined, display an error to the user.
        .then((result) => {
          if (result.error) {
            // The card had an error when trying to attach it to a customer.
            throw result;
          }
          return result;
        })
        // Normalize the result to contain the object returned by Stripe.
        // Add the additional details we need.
        .then((result) => ({
          paymentMethodId: plan.paymentMethodID,
          priceId: plan.priceID,
          subscription: result
        }))
        // Some payment methods require a customer to be on session
        // to complete the payment process. Check the status of the
        // payment intent to handle these actions.
        .then(handlePaymentThatRequiresCustomerAction)
        // If attaching this card to a Customer object succeeds,
        // but attempts to charge the customer fail, you
        // get a requires_payment_method error.
        .then(handleRequiresPaymentMethod)
        // No more actions required. Provision your service for the user.
        .then(onSubscriptionComplete)
        .catch((event) => {
          // An error has happened. Display the failure to the user here.
          // We utilize the HTML element we created.
          if (event.error !== undefined) {
            const message = decodeHTMLEntities(event.error.message);
            enqueueSnackbar(decodeHTMLEntities(message.slice(0, message.indexOf(';'))), {
              variant: 'error'
            });
          }
        });
    } else if (plan.ready === true && plan.id === 'free') {
      const body = { ...session };
      delete body.permission;

      const response = await editItem('users', body.id, {
        ...body,
        expirationTime: moment().add(7, 'days').format(moment.HTML5_FMT.DATE)
      });
      if (response.ok) {
        enqueueSnackbar(t('SPA_Payment_FreeTrial'), {
          variant: 'success'
        });
        const result = await response.json();
        dispatch(sessionActions.update(await result));
        dispatch(sessionActions.abonnement(await true));
        navigate('/', { replace: true });
      } else {
        const error = await response.text();
        const message = error.slice(0, error.indexOf('-'));
        enqueueSnackbar(message, { variant: 'error' });
      }
    }
  }, [plan]);

  useEffectAsync(async () => {
    setPlan({
      id: location.state.id,
      name: location.state.name,
      color: location.state.color,
      price: location.state.price,
      priceID: location.state.priceID,
      paymentMethodID: location.state.paymentMethodID,
      ready: false,
      currency: location.state.currency
    });
  }, []);

  return (
    <Card style={{ backgroundColor: theme && theme.palette.mode !== 'dark' && '#FAFAFA' }}>
      <Stack spacing={3} padding={6}>
        <Typography variant="h6" gutterBottom>
          {t('DB_Summary')}
        </Typography>
        <Stack spacing={2}>
          <Stack spacing={1} direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h7">{t('SPA_Summary_Plan')}</Typography>
            <Label variant="filled" color={plan.color}>
              {plan.name.toUpperCase()}
            </Label>
          </Stack>

          <Stack spacing={1} direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h7">{t('SPA_Summary_StartingFrom')}</Typography>
            <Typography variant="h7">{moment().format('DD/MM/YYYY')}</Typography>
          </Stack>

          <Stack spacing={1} direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h7">
              {t(plan.id === 'free' ? 'SPA_Summary_EndOn' : 'SPA_Summary_RenewalOn')}
            </Typography>
            <Typography variant="h7">{getSubscriptionDate(plan.id)}</Typography>
          </Stack>
        </Stack>

        {plan.id === 'free' ? (
          <Stack spacing={1} direction="row" alignItems="center" justifyContent="end">
            <Typography variant="h4">{t('SPA_FreeDays')}</Typography>
          </Stack>
        ) : (
          <Stack spacing={1} direction="row" alignItems="center" justifyContent="end">
            <Typography variant="h6">{symbolCurrency(plan.currency)}</Typography>
            <Typography variant="h4">{plan.price}</Typography>
            <Typography variant="h4">{t(getSubscriptionPer(plan.id))}</Typography>
          </Stack>
        )}

        <Stack spacing={1}>
          <Typography variant="h6" gutterBottom>
            {t('SPA_Features')}
          </Typography>

          <List>
            <Stack spacing={1}>
              {features.map((feature, index) => (
                <Stack
                  spacing={1}
                  direction="row"
                  alignItems="center"
                  justifyContent="center"
                  key={index}
                >
                  <Icon icon="mdi:check-all" color="#e50e58" width={16} height={16} />
                  <ListItem disablePadding key={index}>
                    <ListItemText primary={feature} />
                  </ListItem>
                </Stack>
              ))}
            </Stack>
          </List>
        </Stack>

        <Stack spacing={3}>
          <LoadingButton
            fullWidth
            size="large"
            type="submit"
            variant="contained"
            style={{ padding: 12 }}
            loading={isSubmitting}
            onClick={() => {
              setIsSubmitting(true);
              handleButtonClick();
            }}
          >
            {plan.id === 'free' ? t('SPA_StartFreeTrial') : t('SPA_Subscribe')}
          </LoadingButton>

          <Stack spacing={0} direction="row" alignItems="center" justifyContent="center">
            <Icon
              icon="mdi:shield"
              color="#22398d"
              width={12}
              height={12}
              style={{ marginRight: 6 }}
            />
            <Typography variant="body2" color="primary">
              {t('SPA_Stripe')}
            </Typography>
            <Link href="https://stripe.com">
              <img
                src="https://upload.wikimedia.org/wikipedia/commons/b/ba/Stripe_Logo%2C_revised_2016.svg"
                alt="Stripe"
                width={48}
              />
            </Link>
          </Stack>
        </Stack>
      </Stack>
    </Card>
  );
}
